import Typography from '@mui/material/Typography';

/**
 * Shared "how account statuses work" explainer, used inside a HowItWorksCard
 * on the Parents / Students / Teachers admin pages.
 *
 * `roleLabel` is one of 'parent' | 'student' | 'teacher' — swaps the copy
 * around wording that differs per role (e.g. Enrolled vs Teaching).
 */
export const AccountLifecycleHelp = ({ roleLabel = 'account' }) => {
    const isTeacher = roleLabel === 'teacher';
    const engagedLabel = isTeacher ? 'Teaching / Not teaching' : 'Enrolled / Not enrolled';
    const activeLabel = isTeacher ? 'still teaching' : 'still on a class roster';

    return (
        <Typography variant="caption" component="div" color="text.secondary">
            Every {roleLabel} account has <strong>two independent statuses</strong>:<br />
            <br />
            <strong>Account status</strong> — <em>Pending approval</em>, <em>Approved</em>,
            <em> Declined</em>, or <em>Archived</em>. Admin-managed. Controls whether the account
            appears on the default active list.<br />
            <strong>Engagement</strong> — <em>{engagedLabel}</em> / <em>On break</em>. Auto-synced
            from live tutoring classes. <em>On break</em> is the only manual override and is
            preserved through class changes.<br />
            <br />
            <strong>Filtering:</strong> use the Account status + Engagement dropdowns above to
            narrow the list — e.g. "all pending {roleLabel}s" or "approved but not enrolled".<br />
            <br />
            <strong>Archiving vs deleting an account:</strong><br />
            <strong>Prefer Archive.</strong> Open the {roleLabel} detail page → <em>Account status</em> card,
            set Account status to <em>Archived</em>. The record is hidden from default lists but
            everything stays intact (history, invoices, class rosters, progress reports, messages).
            Restore any time by setting the status back to <em>Approved</em>.<br />
            <strong>Use "On break"</strong> when {isTeacher ? 'a tutor pauses teaching temporarily' : `a ${roleLabel} pauses mid-year but may return`}.
            The account stays fully active; only their engagement flag changes so admin sees why
            they aren't on a live roster.<br />
            <strong>Decline</strong> is for sign-ups admin rejects — same visibility as archived, but
            records the "we said no" audit trail with the reason.<br />
            <strong>Delete</strong> is only safe for accounts with no history (never enrolled,
            never billed). Once an account has a history, deleting it orphans invoices, reports,
            and roster references. <strong>Prefer archive for real accounts</strong>. Deleting is
            available on the detail page but will be blocked / warned about when history exists.<br />
            <br />
            <strong>Common scenarios:</strong><br />
            • Student leaves mid-year → set engagement to <em>On break</em> (if they may return)
            or archive the account (if they've left for good).<br />
            • Tutor no longer teaching → archive. Historical classes and payouts stay.<br />
            • Parent moves on → archive.<br />
            • Rejected sign-up → decline with a reason.<br />
            • Test / duplicate account never used → delete.<br />
            <br />
            Every status change is logged with timestamp, who changed it, and an optional reason
            — visible as a history timeline on each account's detail page.
        </Typography>
    );
};

export default AccountLifecycleHelp;
